import FormInput from 'components/form-elements/FormInput'
import FormMultipleSelect from 'components/form-elements/FormMultipleSelect'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import {
  familyCaregiverRoleOptions,
  professionalCaregiverRoleOptions,
  TraineeTeamRole,
} from 'types'
import { Option } from './FormSelect'

export const PROFESSIONAL_ROLE_FIELD = 'professionalRole'
export const FAMILY_ROLE_FIELD = 'familyRole'
export const OTHER_ROLE_FIELD = 'otherRole'

/** Plain option shape stored in the role form fields. */
export type RoleFieldOption = { label: string; value: string }

const familyOnlyValues = familyCaregiverRoleOptions
  .map((option) => option.value)
  .filter((value) => value !== TraineeTeamRole.other)

const toRoleFieldOption = (options: Option[], value: string): RoleFieldOption => {
  const option = options.find((item) => item.value === value)
  return { label: String(option?.label ?? value), value: String(option?.value ?? value) }
}

/**
 * Splits a flat list of role values into the two category fields. A member can only
 * belong to one category (enforced on the backend), so the whole list goes into the
 * family bucket if it contains any family-specific role, otherwise into the professional
 * one. The shared `other` value follows whichever bucket is chosen.
 */
export const splitRolesIntoCategories = (
  roles?: string[] | null,
): { [PROFESSIONAL_ROLE_FIELD]: RoleFieldOption[]; [FAMILY_ROLE_FIELD]: RoleFieldOption[] } => {
  if (!roles?.length) {
    return { [PROFESSIONAL_ROLE_FIELD]: [], [FAMILY_ROLE_FIELD]: [] }
  }

  const isFamily = roles.some((role) => familyOnlyValues.includes(role as TraineeTeamRole))
  const options = isFamily ? familyCaregiverRoleOptions : professionalCaregiverRoleOptions
  const mapped = roles.map((role) => toRoleFieldOption(options, role))

  return isFamily
    ? { [PROFESSIONAL_ROLE_FIELD]: [], [FAMILY_ROLE_FIELD]: mapped }
    : { [PROFESSIONAL_ROLE_FIELD]: mapped, [FAMILY_ROLE_FIELD]: [] }
}

/** Merges both category selects back into a single flat list of role values for the API. */
export const combineRoleValues = (professionalRole?: Option[], familyRole?: Option[]): string[] =>
  [...(professionalRole ?? []), ...(familyRole ?? [])].map((role) => String(role.value))

const hasOtherRole = (roles?: Option[]) =>
  !!roles?.some((role) => role.value === TraineeTeamRole.other)

type Props = {
  /** Disable both selects and the Other field (e.g. for registered/active members). */
  disabled?: boolean
}

/**
 * Two mutually-exclusive role selects (Professional / Family Caregiver Role). Selecting a
 * value in one disables the other until it is cleared. Shows the manual "Other Role" input
 * when either select contains the `Other` option. Relies on the surrounding FormProvider
 * exposing the `professionalRole`, `familyRole` and `otherRole` fields.
 */
export function CaregiverRoleFields({ disabled = false }: Props) {
  const { control } = useFormContext()
  const professionalRole = useWatch({ control, name: PROFESSIONAL_ROLE_FIELD }) as
    | Option[]
    | undefined
  const familyRole = useWatch({ control, name: FAMILY_ROLE_FIELD }) as Option[] | undefined

  const hasProfessional = !!professionalRole?.length
  const hasFamily = !!familyRole?.length
  const showOther = hasOtherRole(professionalRole) || hasOtherRole(familyRole)

  return (
    <>
      <FormMultipleSelect
        name={PROFESSIONAL_ROLE_FIELD}
        label="Professional Caregiver Role"
        options={professionalCaregiverRoleOptions}
        defaultValue={[]}
        isLoading={false}
        placeholder="Select role(s)"
        disabled={disabled || hasFamily}
      />
      <FormMultipleSelect
        name={FAMILY_ROLE_FIELD}
        label="Family Caregiver Role"
        options={familyCaregiverRoleOptions}
        defaultValue={[]}
        isLoading={false}
        placeholder="Select role(s)"
        disabled={disabled || hasProfessional}
      />
      {showOther && (
        <FormInput name={OTHER_ROLE_FIELD} label="Other Role" fullWidth disabled={disabled} />
      )}
    </>
  )
}
